import { useState, useCallback } from 'react';
import { ChatMessage, QuestionResponse } from '../types';
import { ERROR_MESSAGES, GUIDANCE_MESSAGES } from '../utils/constants';
import { apiRequest } from '../config/api';
import { generateId } from '../utils/helpers';

interface UseChatReturn {
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  guidance: string | null;
  retryAfter: number | null;
  askQuestion: (question: string, documentId?: number | string | null) => Promise<void>;
  clearHistory: () => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
}

export const useChat = (): UseChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guidance, setGuidance] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  const askQuestion = useCallback(async (question: string, documentId?: number | string | null) => {
    if (!question.trim()) return;

    setLoading(true);
    setError(null);
    setGuidance(null);
    setRetryAfter(null);

    try {
      const data: QuestionResponse = await apiRequest('/rag/ask', {
        method: 'POST',
        body: JSON.stringify({
          question: question,
          document_id: documentId || null,
          match_threshold: 0.1,
          match_count: 5
        }),
      });
      
      // Handle new fallback system response format
      if (data.success === false) {
        // Handle error response with guidance
        setError(data.answer || ERROR_MESSAGES.ASK_QUESTION_FAILED);
        setGuidance(data.guidance || GUIDANCE_MESSAGES.UNKNOWN);
        setRetryAfter(data.retry_after || null);
        return;
      }
      
      // Add to chat history
      const newMessage: ChatMessage = {
        id: generateId(),
        question: question,
        answer: data.answer,
        timestamp: new Date(),
        document_id: data.document_id,
        chunks_used: data.chunks_used,
        processing_time: data.processing_time,
        provider_used: data.provider_used,
        cached: data.cached || false,
        fallback_reason: data.fallback_reason
      };
      
      setMessages(prev => [newMessage, ...prev]);
    } catch (err: any) {
      console.error('Chat API Error:', err);

      // Check for Safari mobile "Load failed" error
      const isLoadFailed = err instanceof TypeError &&
        (err.message?.includes('Load failed') || err.message?.includes('Failed to fetch'));
      const isSafariMobile = /safari/i.test(navigator.userAgent) &&
        /iphone|ipad|ipod/i.test(navigator.userAgent) &&
        !/chrome/i.test(navigator.userAgent);

      if (isLoadFailed && isSafariMobile) {
        setError('Connection lost while processing. Please keep the app in foreground and try again.');
        setGuidance('Safari on iOS pauses network requests when you leave the app. Keep the screen on while waiting for responses.');
        setRetryAfter(null);
      } else if (err.response?.data) {
        // Handle structured error response
        const errorData = err.response.data;
        setError(errorData.answer || errorData.message || ERROR_MESSAGES.ASK_QUESTION_FAILED);
        setGuidance(errorData.guidance || GUIDANCE_MESSAGES.UNKNOWN);
        setRetryAfter(errorData.retry_after || null);
      } else {
        setError(err.message || ERROR_MESSAGES.ASK_QUESTION_FAILED);
        setGuidance(GUIDANCE_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const clearHistory = useCallback(() => {
    setMessages([]);
  }, []);

  const addMessage = useCallback((messageData: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...messageData,
      id: generateId(),
      timestamp: new Date()
    };
    setMessages(prev => [newMessage, ...prev]);
  }, []);

  return {
    messages,
    loading,
    error,
    guidance,
    retryAfter,
    askQuestion,
    clearHistory,
    addMessage
  };
}; 